import React, { useMemo } from 'react';
import { find, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import {
  Alert,
  Box,
  Button,
  CardContent,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  Paper,
  PasteIcon,
  Typography,
} from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';

import { AvailityHeaderLogo, Footer, MessageDisplay } from '@/components';
import { useAppContext } from '@/context';
import { getHiddenValueCallbackById } from '@/global';
import { useFRCallback } from '@/hooks';
import { TwoFAIcon03 } from '@/static';

const specificEnv = getSpecificEnv();

const faqLink =
  'https://essentials.availity.com/availity/help-open/source/portal_providers/account_administration/2-step/_topics/c_faqs_for_2step_authentication.html';

function TwoFAAuthenticatorSetup({ ...props }) {
  const { stepToRender, setCurrentStep } = props;
  const {
    callbacks = [],
    confirmationCallbackIndex,
    confirmationCallbackOptions,
    textOutputCallback,
  } = useFRCallback(stepToRender);
  const { messages = [], addMessage, setMessages } = useAppContext();

  // get qrcode values
  const qrCodeFullSecret = find(
    textOutputCallback?.output,
    (outputObj) => outputObj?.name?.toLowerCase() === 'message'
  )?.value;

  // insert env into qrcode string if in nonprod
  let qrCodeToUse = qrCodeFullSecret;
  if (specificEnv !== 'prod') {
    const insertionIndex = qrCodeFullSecret?.indexOf('Availity');
    const prefix = qrCodeFullSecret?.slice(0, insertionIndex);
    const suffix = qrCodeFullSecret?.slice(insertionIndex);
    qrCodeToUse = `${prefix}${specificEnv?.toUpperCase()}-${suffix}`;
  }

  const totpSecretKey = useMemo(() => {
    let result = '';
    if (callbacks) {
      const secretKeyCallback = getHiddenValueCallbackById(callbacks, 'totpsecret');
      result =
        secretKeyCallback &&
        find(secretKeyCallback.output, (outputObj) => outputObj?.name?.toLowerCase() === 'value')?.value;
    }

    return result;
  }, [callbacks]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(totpSecretKey);
      addMessage({
        type: 'success',
        text: 'Secret key copied successfully!',
        icon: 'icon-attention-circle',
        timeout: 5 * 1000, // 5 seconds
        dismissible: true,
      });
    } catch (error) {
      if (error) {
        addMessage({
          type: 'danger',
          text: 'Failed to copy the secret key. Please try again.',
          icon: 'icon-attention-circle',
          timeout: 5 * 1000, // 5 seconds
          dismissible: true,
        });
      }
    }
  }

  const handleNavigation = (selected) => {
    setMessages([]);
    const nextStep = { ...stepToRender };
    const newCallbacks = [...callbacks];

    // set the confirmation callback input based off the index of the selected button navigation in the callback
    if (selected === 'next') {
      const nextButtonIndex = findIndex(confirmationCallbackOptions, (button) => button?.toLowerCase() === 'next');

      newCallbacks[confirmationCallbackIndex].input[0] = {
        ...newCallbacks[confirmationCallbackIndex].input[0],
        value: nextButtonIndex,
      };
    } else if (selected === 'back') {
      const backButtonIndex = findIndex(confirmationCallbackOptions, (button) => button?.toLowerCase() === 'back');

      newCallbacks[confirmationCallbackIndex].input[0] = {
        ...newCallbacks[confirmationCallbackIndex].input[0],
        value: backButtonIndex,
      };
    }

    // set the newCallbacks in the newStep
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // update the currentStep to trigger navigation
    setCurrentStep({ ...nextStep });
  };

  return (
    <Grid
      id="2fa-totp-enrollment-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="2fa-start-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '750px', lg: '1000px' } }}
          >
            <CardContent>
              <Grid display="flex" flexDirection="row" flexWrap="wrap">
                <Grid size={{ sm: 12, md: 4 }} sx={{ marginBottom: { xs: 2, md: 0 }, paddingRight: 2 }}>
                  <Box textAlign="center" mb={2}>
                    <img
                      src={TwoFAIcon03}
                      alt="2FA Icon"
                      style={{
                        maxWidth: '45%',
                      }}
                    />
                  </Box>
                  <Typography variant="subtitle1" mb={2}>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      Hard Token Authenticator Set Up
                    </Box>
                  </Typography>
                  <Typography variant="subtitle1" mb={2}>
                    Follow your token&apos;s set up instructions. Depending on the device requirements, use the QR code
                    or secret key to finish set up.
                  </Typography>
                  <Typography variant="subtitle1" mb={2}>
                    This method may require assistance from your organization&apos;s IT administrator.
                  </Typography>
                  <Box mt={3}>
                    <Link
                      href={faqLink}
                      target="_blank"
                      loadApp={false}
                      iconPosition="end"
                      rel="noopener noreferrer"
                      aria-label="Get answers to common questions (opens PDF in a new window)"
                    >
                      2-Step Authentication FAQs
                    </Link>
                  </Box>
                </Grid>
                <Grid
                  display="flex"
                  flexDirection="column"
                  size={{ md: 8 }}
                  sx={{ borderLeft: { sm: 0, md: 1 }, borderColor: { md: '#D7DAE0' } }}
                >
                  <Box pl={2}>
                    <Typography variant="h1" mb={1}>
                      Authenticator Set Up
                    </Typography>
                    <Typography variant="h4" component="h2" mb={2}>
                      Follow these steps to set up an authenticator app
                    </Typography>
                  </Box>
                  <Grid display="flex" flexDirection="column" flexGrow={1} pl={2}>
                    <List
                      component="ol"
                      sx={{
                        listStyleType: 'decimal',
                        listStylePosition: 'outside',
                        paddingX: 2,
                      }}
                    >
                      <ListItem divider={false} sx={{ display: 'list-item' }}>
                        <Typography component="span" variant="subtitle1">
                          Install an authenticator application (such as Google Authenticator) on your mobile device or
                          computer.
                        </Typography>
                      </ListItem>
                      <ListItem divider={false} sx={{ display: 'list-item' }}>
                        <Box>
                          <Grid display="flex" flexDirection="row" flexWrap="wrap" mb={2} alignItems="center">
                            <Grid size={{ xs: 12, lg: 5 }}>
                              <Paper
                                sx={{
                                  padding: 2,
                                  display: 'inline-block',
                                  textAlign: 'center',
                                  marginRight: 2,
                                  marginBottom: { xs: 2, lg: 0 },
                                }}
                              >
                                <QRCodeSVG value={qrCodeToUse} size={165} />
                              </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, lg: 7 }}>
                              <Typography variant="subtitle1">
                                Scan the QR code using your authentication app or device&apos;s camera
                              </Typography>
                            </Grid>
                          </Grid>
                          <Typography variant="subtitle1" mb={2}>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                              OR
                            </Box>{' '}
                            enter this secret key into your time-based authentication device:
                          </Typography>
                          <Grid display="flex" size={{ xs: 12 }} sx={{ alignItems: 'baseline' }}>
                            <Grid size={{ xs: 12, md: 10 }}>
                              <Alert
                                display="flex"
                                icon={false}
                                severity="info"
                                sx={{ padding: 2, justifyContent: 'center' }}
                              >
                                <Box sx={{ fontWeight: 'bold' }}>{totpSecretKey}</Box>
                              </Alert>
                            </Grid>
                            <Grid size={1} sx={{ marginLeft: 1 }}>
                              <IconButton title="Copy Backup Code" onClick={() => handleCopy()}>
                                <PasteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                          <Box mt={messages?.length > 0 ? 2 : 0}>
                            <MessageDisplay />
                          </Box>
                        </Box>
                      </ListItem>
                      <ListItem divider={false} sx={{ display: 'list-item' }}>
                        <Typography variant="subtitle1" mb={2}>
                          Once you&apos;ve completed these steps, please continue.
                        </Typography>
                      </ListItem>
                    </List>
                    <Box textAlign="right" mt="auto">
                      <Button color="primary" onClick={() => handleNavigation('next')}>
                        I&apos;ve completed these steps
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
}

TwoFAAuthenticatorSetup.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default TwoFAAuthenticatorSetup;
